/**
 * Post stack for the Alpha OPS vault: render -> bloom (neon only) -> AgX tone map
 * -> FXAA -> fisheye + grade.
 *
 * The fisheye is a true equisolid lens (r = 2f sin(theta / 2)), the same projection
 * the Blender look-dev camera used. The scene is rendered through an ordinary,
 * wider-than-needed perspective camera into an oversized target; the last pass
 * maps every screen pixel to the ray the fisheye would have shot and reads that
 * ray out of the perspective image. Everything before the last pass therefore
 * runs at the source size, the last pass at the canvas size.
 */
import * as THREE from 'three';
import { EffectComposer } from 'three/examples/jsm/postprocessing/EffectComposer.js';
import { OutputPass } from 'three/examples/jsm/postprocessing/OutputPass.js';
import { RenderPass } from 'three/examples/jsm/postprocessing/RenderPass.js';
import { ShaderPass } from 'three/examples/jsm/postprocessing/ShaderPass.js';
import { UnrealBloomPass } from 'three/examples/jsm/postprocessing/UnrealBloomPass.js';
import { FXAAShader } from 'three/examples/jsm/shaders/FXAAShader.js';

const FisheyeGradeShader = {
  name: 'AlphaOpsFisheyeGrade',
  uniforms: {
    tDiffuse: { value: null },
    focal: { value: 1.11 },
    strength: { value: 1 },
    srcTan: { value: 1.6 },
    aspect: { value: 16 / 9 },
    contrast: { value: 0.12 },
    grain: { value: 0.012 },
    time: { value: 0 },
    resolution: { value: new THREE.Vector2(1, 1) },
  },
  vertexShader: `
    varying vec2 vUv;
    void main() {
      vUv = uv;
      gl_Position = projectionMatrix * modelViewMatrix * vec4( position, 1.0 );
    }`,
  fragmentShader: `
    uniform sampler2D tDiffuse;
    uniform float focal;
    uniform float strength;
    uniform float srcTan;
    uniform float aspect;
    uniform float contrast;
    uniform float grain;
    uniform float time;
    uniform vec2 resolution;
    varying vec2 vUv;
    float hash( vec2 p ) {
      vec3 p3 = fract( vec3( p.xyx ) * 0.1031 );
      p3 += dot( p3, p3.yzx + 33.33 );
      return fract( ( p3.x + p3.y ) * p3.z );
    }
    void main() {
      // screen point on the "sensor", in half-widths (square pixels)
      vec2 s = ( vUv - 0.5 ) * 2.0;
      s.y /= aspect;
      float r = length( s );
      float thetaFish = 2.0 * asin( clamp( r / ( 2.0 * focal ), 0.0, 1.0 ) );
      float thetaRect = atan( r / focal );
      float theta = mix( thetaRect, thetaFish, strength );
      vec2 dir = r > 1e-6 ? s / r : vec2( 0.0 );
      vec2 q = dir * tan( theta ) / srcTan;              // source NDC (y still in half-widths)
      vec2 uv = 0.5 + 0.5 * vec2( q.x, q.y * aspect );
      vec3 col = texture2D( tDiffuse, uv ).rgb;
      col *= step( 0.0, uv.x ) * step( uv.x, 1.0 ) * step( 0.0, uv.y ) * step( uv.y, 1.0 );
      // a gentle S-curve (Blender's AgX "Medium High Contrast" look sits above three's base AgX)
      vec3 sc = col * col * ( 3.0 - 2.0 * col );
      col = mix( col, sc, contrast );
      float vig = smoothstep( 1.25, 0.35, length( ( vUv - 0.5 ) * vec2( 1.0, 0.85 ) ) * 2.0 );
      col *= mix( 0.55, 1.0, vig );
      float g = hash( floor( vUv * resolution * 0.5 ) + floor( time * 12.0 ) * 1.7 ) - 0.5;
      col += g * grain * ( 1.0 - 0.6 * col );
      gl_FragColor = vec4( col, 1.0 );
    }`,
};

export function createPost(renderer, scene, camera, opts) {
  // plain half-float target: MSAA on float buffers is where some Windows/ANGLE drivers fall over
  const target = new THREE.WebGLRenderTarget(1, 1, { type: THREE.HalfFloatType });
  const composer = new EffectComposer(renderer, target);
  composer.addPass(new RenderPass(scene, camera));
  // A tight glow on the neon only. The stock high-pass keys on luminance, where pure red is dim
  // (0.21 of its value) and white steel glints are bright, so the steel bloomed and the neon
  // didn't. Key on the strongest channel instead, and clip the input so a glint can't flare.
  const bloom = new UnrealBloomPass(new THREE.Vector2(1, 1), 0.11, 0.1, 0.45);
  bloom.highPassUniforms.smoothWidth.value = 0.25;
  bloom.materialHighPassFilter.fragmentShader = `
    uniform sampler2D tDiffuse;
    uniform vec3 defaultColor;
    uniform float defaultOpacity;
    uniform float luminosityThreshold;
    uniform float smoothWidth;
    varying vec2 vUv;
    void main() {
      vec3 c = min( texture2D( tDiffuse, vUv ).rgb, vec3( 1.2 ) );
      // saturated light (the neon) passes, near-white light (steel glints) is held back
      float sat = max( c.r, max( c.g, c.b ) ) - min( c.r, min( c.g, c.b ) );
      float v = sat;
      float a = smoothstep( luminosityThreshold, luminosityThreshold + smoothWidth, v );
      gl_FragColor = mix( vec4( defaultColor.rgb, defaultOpacity ), vec4( c, 1.0 ), a );
    }`;
  bloom.materialHighPassFilter.needsUpdate = true;
  if (opts.bloom !== false) composer.addPass(bloom);
  composer.addPass(new OutputPass());
  const fxaa = new ShaderPass(FXAAShader);
  composer.addPass(fxaa);
  const grade = new ShaderPass(FisheyeGradeShader);
  if (opts.grade === false) grade.uniforms.contrast.value = 0;
  composer.addPass(grade);

  return {
    /** canvas size in device pixels, source (oversized) size in device pixels, and the lens */
    setSize(canvasW, canvasH, srcW, srcH, lens) {
      composer.setPixelRatio(1);
      composer.setSize(srcW, srcH);
      fxaa.uniforms.resolution.value.set(1 / srcW, 1 / srcH);
      grade.uniforms.resolution.value.set(canvasW, canvasH);
      grade.uniforms.aspect.value = canvasW / canvasH;
      grade.uniforms.focal.value = lens.focal;
      grade.uniforms.strength.value = lens.strength;
      grade.uniforms.srcTan.value = lens.srcTan;
    },
    render(time) {
      grade.uniforms.time.value = time;
      composer.render();
    },
    dispose() {
      bloom.dispose();
      fxaa.dispose();
      grade.dispose();
      composer.dispose();
      target.dispose();
    },
  };
}
